import {
  createSlice,
  PayloadAction,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { Vignette, VignetteAnswerOption } from '../../types/Vignette';
import { UserExam } from '../../types/User';
import { RootState } from '../../store/store';

type VignettesStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// Configure the adapter with a sortComparer function
const vignettesAdapter = createEntityAdapter<Vignette>({
  // // Commented out to let the backend handle the sorting
  // // Sort vignettes by firstSessionOrd first (null values at end), then by yield
  // sortComparer: (a, b) => {
  //   // First sort by yield descending
  //   const yieldDiff = b.yield - a.yield;
  //   if (yieldDiff !== 0) return yieldDiff;
  //   // If yields are equal, sort by createdAt ascending
  //   const createdAtA = a.createdAt || '';
  //   const createdAtB = b.createdAt || '';
  //   return createdAtA.localeCompare(createdAtB);
  // },
});

interface VignetteState
  extends ReturnType<typeof vignettesAdapter.getInitialState> {
  status: VignettesStatus;
  error: string | null;
}

const initialState: VignetteState = vignettesAdapter.getInitialState({
  status: 'idle',
  error: null,
});

export const vignettesSlice = createSlice({
  name: 'vignettes',
  initialState,
  reducers: {
    vignettesLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },
    setVignettes: (state, action: PayloadAction<Vignette[]>) => {
      vignettesAdapter.setAll(state, action.payload);
      state.status = 'succeeded';
    },
    vignetteReceived: (state, action: PayloadAction<Vignette>) => {
      vignettesAdapter.upsertOne(state, action.payload);
      state.status = 'succeeded';
      state.error = null;
    },
    shiftVignette: (state) => {
      const allVignettes = vignettesAdapter.getSelectors().selectAll(state);
      if (allVignettes.length > 0) {
        vignettesAdapter.removeOne(state, allVignettes[0].id);
      }
    },
    vignetteDeleted: (state, action: PayloadAction<string>) => {
      vignettesAdapter.removeOne(state, action.payload);
      state.status = 'succeeded';
      state.error = null;
    },
    vignettesFailed: (state, action: PayloadAction<string | null>) => {
      state.status = 'failed';
      state.error = action.payload ?? 'Failed to load vignettes';
    },
    clearVignettes: (state) => {
      vignettesAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
    },
    replaceVignetteById: (
      state,
      action: PayloadAction<{
        vignetteId: string;
        newYieldNumber: number;
        newFirstSessionOrd: number;
        newExams: UserExam[];
      }>
    ) => {
      const { vignetteId, newYieldNumber, newFirstSessionOrd, newExams } =
        action.payload;

      const existingVignette = state.entities[vignetteId];
      if (existingVignette) {
        const updatedVignette = {
          ...existingVignette,
          yield: newYieldNumber,
          firstSessionOrd: newFirstSessionOrd,
          exams: newExams,
        };
        vignettesAdapter.updateOne(state, {
          id: vignetteId,
          changes: updatedVignette,
        });
      }
    },
    replaceVignetteContentById: (
      state,
      action: PayloadAction<{
        vignetteId: string;
        contentId: string;
        newQuestion: string;
        newExplanation: string;
        newExhibit: string;
        newLearningObjective: string;
        newAnswerOptions: VignetteAnswerOption[];
      }>
    ) => {
      const {
        vignetteId,
        contentId,
        newQuestion,
        newExplanation,
        newExhibit,
        newLearningObjective,
        newAnswerOptions,
      } = action.payload;

      const existingVignette = state.entities[vignetteId];
      if (existingVignette) {
        const contentIndex = existingVignette.contents.findIndex(
          (content) => content.id === contentId
        );
        if (contentIndex !== -1) {
          const updatedContent = {
            ...existingVignette.contents[contentIndex],
            question: newQuestion,
            explanation: newExplanation,
            exhibit: newExhibit,
            learningObjective: newLearningObjective,
            answerOptions: newAnswerOptions,
          };
          const clonedContents = [...existingVignette.contents];
          clonedContents[contentIndex] = updatedContent;

          vignettesAdapter.updateOne(state, {
            id: vignetteId,
            changes: {
              contents: clonedContents,
            },
          });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      vignettesAdapter.removeAll(state);
      state.status = 'idle';
      state.error = null;
    });
  },
});

export const {
  vignettesLoading,
  setVignettes,
  vignetteReceived,
  shiftVignette,
  vignetteDeleted,
  vignettesFailed,
  clearVignettes,
  replaceVignetteById,
  replaceVignetteContentById,
} = vignettesSlice.actions;

export const vignettesSelectors = vignettesAdapter.getSelectors(
  (state: { vignettes: VignetteState }) =>
    state.vignettes || vignettesAdapter.getInitialState()
);

// Selector to get the active vignette
export const selectActiveVignette = (state: RootState) => {
  const vignettes = vignettesSelectors.selectAll(state);
  const activeQuestionIndex = state.studyPhase.activeQuestionIndex;
  return vignettes[activeQuestionIndex] || null;
};

// Selector to get the active vignette content (first content of active vignette)
export const selectActiveVignetteContent = (state: RootState) => {
  const activeVignette = selectActiveVignette(state);
  return activeVignette?.contents[0] || null;
};

export default vignettesSlice.reducer;

// Import the logout action from authSlice
import { logout } from '../auth/authSlice';
